/**
 * Households API — typed wrappers around `/api/household/*` and
 * `/api/households/*`.
 *
 * Ported from legacy `frontend/src/services/householdService.ts` +
 * `frontend/src/services/householdsService.ts`.
 *
 * Endpoint map (unchanged backend):
 *
 *   GET    /api/household                       → { household }
 *   PATCH  /api/household                       → { household, storesDiscovered?, storesImported? }
 *   POST   /api/household/discover-stores       → { stores }
 *   POST   /api/household/import-stores         → { imported, storeIds }
 *   GET    /api/household/selected-stores       → { storeIds }
 *   PUT    /api/household/selected-stores       → { storeIds }
 *   GET    /api/household/users                 → { users }
 *   DELETE /api/household/users/:userId
 *   GET    /api/household/invites               → { invites }
 *   POST   /api/household/invites               → { invite, inviteUrl, emailSent }
 *   DELETE /api/household/invites/:inviteId     → cancel pending invite
 *
 *   GET    /api/households                      → { households }
 *   POST   /api/households/join                 → membership mutation envelope
 *   POST   /api/households/switch               → membership mutation envelope
 *   POST   /api/households/leave                → membership mutation envelope
 *   DELETE /api/households/:householdId         → membership mutation envelope
 *
 * T7 uses `fetchCurrent()` + `list()` for the auth guard / household
 * membership check. The mutation methods here are scaffolded so T8 screens
 * (SettingsUsers / HouseholdSettings) can consume them without re-porting.
 */

import { z } from 'zod';
import type { HttpClient } from './client';
import {
  type CreateInviteRequest,
  type CreateInviteResponse,
  type DiscoveredStoresResponse,
  type Household,
  type HouseholdBrandPreferencesResponse,
  type HouseholdBrandPreferenceValue,
  type HouseholdCatalogPreferenceSignalsResponse,
  type HouseholdSystemProductPreferencesResponse,
  type HouseholdSystemProductPreferenceValue,
  type HouseholdInvitesResponse,
  type HouseholdMembershipMutationResponse,
  type HouseholdResponse,
  type HouseholdSummary,
  type HouseholdUpdateRequest,
  type HouseholdUpdateResponse,
  type HouseholdUsersResponse,
  type HouseholdsResponse,
  type ImportStoresRequest,
  type ImportStoresResponse,
  type JoinHouseholdRequest,
  type SelectedStoresResponse,
  type StorePreferencesResponse,
  type SwitchHouseholdRequest,
  type UpdateMemberFieldsRequest,
  createInviteResponseSchema,
  discoveredStoresResponseSchema,
  householdBrandPreferencesResponseSchema,
  householdCatalogPreferenceSignalsResponseSchema,
  householdInvitesResponseSchema,
  householdMembershipMutationResponseSchema,
  householdResponseSchema,
  householdSystemProductPreferencesResponseSchema,
  householdUpdateResponseSchema,
  householdUsersResponseSchema,
  householdsResponseSchema,
  importStoresResponseSchema,
  selectedStoresResponseSchema,
  storePreferencesResponseSchema,
} from '../types/household';

const successSchema = z.object({ success: z.boolean() });

const SINGULAR_BASE = '/api/household';
const PLURAL_BASE = '/api/households';

export class HouseholdsApi {
  constructor(private readonly http: HttpClient) {}

  // -------------------------------------------------------------------------
  // Current household (from legacy `householdService.ts`)
  // -------------------------------------------------------------------------

  /**
   * Fetch the *current* household (the one the access token is scoped to).
   * Returns the unwrapped `Household` value — callers rarely want the
   * response envelope itself.
   */
  async fetchCurrent(): Promise<Household> {
    const resp: HouseholdResponse = await this.http.get(
      SINGULAR_BASE,
      householdResponseSchema
    );
    return resp.household;
  }

  /**
   * Update current household settings. Returns the updated household plus an
   * optional `storesImported` summary when `discoverStores: true` was passed.
   */
  updateCurrent(input: HouseholdUpdateRequest): Promise<HouseholdUpdateResponse> {
    return this.http.patch(
      SINGULAR_BASE,
      input,
      householdUpdateResponseSchema
    );
  }

  discoverNearbyStores(input: {
    latitude: number;
    longitude: number;
    radiusKm?: number;
  }): Promise<DiscoveredStoresResponse> {
    return this.http.post(
      `${SINGULAR_BASE}/discover-stores`,
      input,
      discoveredStoresResponseSchema
    );
  }

  importStores(input: ImportStoresRequest): Promise<ImportStoresResponse> {
    return this.http.post(
      `${SINGULAR_BASE}/import-stores`,
      input,
      importStoresResponseSchema
    );
  }

  getSelectedStores(): Promise<SelectedStoresResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/selected-stores`,
      selectedStoresResponseSchema
    );
  }

  setSelectedStores(storeIds: string[]): Promise<SelectedStoresResponse> {
    return this.http.put(
      `${SINGULAR_BASE}/selected-stores`,
      { storeIds },
      selectedStoresResponseSchema
    );
  }

  // -------------------------------------------------------------------------
  // T12.2 — per-store category affinity. The full preference entries
  // (`GET /store-preferences`) carry the declared `categories` per selected
  // store; the PUT writes one store's category set (empty array clears).
  // -------------------------------------------------------------------------

  getStorePreferences(): Promise<StorePreferencesResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/store-preferences`,
      storePreferencesResponseSchema
    );
  }

  setStoreCategories(storeId: string, categories: string[]): Promise<void> {
    return this.http
      .put(
        `${SINGULAR_BASE}/selected-stores/${encodeURIComponent(storeId)}/categories`,
        { categories },
        successSchema
      )
      .then(() => undefined);
  }

  // -------------------------------------------------------------------------
  // T12.2 — household-wide brand like/avoid. Mirrors the per-product brand
  // preference shape one level up; a brand is `like` OR `avoid`, never both.
  // -------------------------------------------------------------------------

  getBrandPreferences(): Promise<HouseholdBrandPreferencesResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/brand-preferences`,
      householdBrandPreferencesResponseSchema
    );
  }

  linkBrandPreference(
    brandId: string,
    preference: HouseholdBrandPreferenceValue
  ): Promise<void> {
    return this.http
      .post(
        `${SINGULAR_BASE}/brand-preferences`,
        { brandId, preference },
        successSchema
      )
      .then(() => undefined);
  }

  setBrandPreference(
    brandId: string,
    preference: HouseholdBrandPreferenceValue
  ): Promise<void> {
    return this.http
      .patch(
        `${SINGULAR_BASE}/brand-preferences/${encodeURIComponent(brandId)}`,
        { preference },
        successSchema
      )
      .then(() => undefined);
  }

  unlinkBrandPreference(brandId: string): Promise<void> {
    return this.http.delete(
      `${SINGULAR_BASE}/brand-preferences/${encodeURIComponent(brandId)}`
    );
  }

  // -------------------------------------------------------------------------
  // T12.2 (step 8) — household-wide system-product like/avoid. The exact mirror
  // of the brand-preference wrappers; a system product is `like` OR `avoid`,
  // never both. The read embeds the SystemProduct name (large catalog).
  // -------------------------------------------------------------------------

  getSystemProductPreferences(): Promise<HouseholdSystemProductPreferencesResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/systemproduct-preferences`,
      householdSystemProductPreferencesResponseSchema
    );
  }

  linkSystemProductPreference(
    systemProductId: string,
    preference: HouseholdSystemProductPreferenceValue
  ): Promise<void> {
    return this.http
      .post(
        `${SINGULAR_BASE}/systemproduct-preferences`,
        { systemProductId, preference },
        successSchema
      )
      .then(() => undefined);
  }

  setSystemProductPreference(
    systemProductId: string,
    preference: HouseholdSystemProductPreferenceValue
  ): Promise<void> {
    return this.http
      .patch(
        `${SINGULAR_BASE}/systemproduct-preferences/${encodeURIComponent(systemProductId)}`,
        { preference },
        successSchema
      )
      .then(() => undefined);
  }

  unlinkSystemProductPreference(systemProductId: string): Promise<void> {
    return this.http.delete(
      `${SINGULAR_BASE}/systemproduct-preferences/${encodeURIComponent(systemProductId)}`
    );
  }

  // -------------------------------------------------------------------------
  // T12.5.1 (C) — household catalog preference signals (read-only). The
  // per-product brand + product preferences (preferred / substitute / exclude,
  // carrying the value), rolled up to the household; the Deals/Planning surfaces
  // render the full stacked badge set from these.
  // -------------------------------------------------------------------------

  getCatalogPreferenceSignals(): Promise<HouseholdCatalogPreferenceSignalsResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/catalog-preference-signals`,
      householdCatalogPreferenceSignalsResponseSchema
    );
  }

  // -------------------------------------------------------------------------
  // T12.2 — per-member age group / dietary exclusions.
  // Manager-scoped on the backend; mirrors `removeUser`'s id-in-path shape.
  // -------------------------------------------------------------------------

  updateMemberFields(
    userId: string,
    input: UpdateMemberFieldsRequest
  ): Promise<void> {
    return this.http
      .patch(
        `${SINGULAR_BASE}/users/${encodeURIComponent(userId)}`,
        input,
        successSchema
      )
      .then(() => undefined);
  }

  // -------------------------------------------------------------------------
  // Users + invites (from legacy `householdsService.ts`)
  // -------------------------------------------------------------------------

  fetchUsers(): Promise<HouseholdUsersResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/users`,
      householdUsersResponseSchema
    );
  }

  removeUser(userId: string): Promise<void> {
    return this.http.delete(
      `${SINGULAR_BASE}/users/${encodeURIComponent(userId)}`
    );
  }

  fetchInvites(): Promise<HouseholdInvitesResponse> {
    return this.http.get(
      `${SINGULAR_BASE}/invites`,
      householdInvitesResponseSchema
    );
  }

  createInvite(input: CreateInviteRequest): Promise<CreateInviteResponse> {
    return this.http.post(
      `${SINGULAR_BASE}/invites`,
      input,
      createInviteResponseSchema
    );
  }

  /**
   * Cancel a pending invite (hard-delete). Manager-only on the backend.
   * Mirrors `removeUser` — DELETE returns void, callers should refresh
   * `fetchInvites()` (the hook does this via query invalidation).
   */
  cancelInvite(inviteId: string): Promise<void> {
    return this.http.delete(
      `${SINGULAR_BASE}/invites/${encodeURIComponent(inviteId)}`
    );
  }

  // -------------------------------------------------------------------------
  // Multi-household memberships (from legacy `householdsService.ts`)
  // -------------------------------------------------------------------------

  /**
   * List every household the authenticated user belongs to (active membership).
   * Returns the unwrapped summary list.
   */
  async list(): Promise<HouseholdSummary[]> {
    const resp: HouseholdsResponse = await this.http.get(
      PLURAL_BASE,
      householdsResponseSchema
    );
    return resp.households;
  }

  join(
    input: JoinHouseholdRequest
  ): Promise<HouseholdMembershipMutationResponse> {
    return this.http.post(
      `${PLURAL_BASE}/join`,
      input,
      householdMembershipMutationResponseSchema
    );
  }

  switch(
    input: SwitchHouseholdRequest
  ): Promise<HouseholdMembershipMutationResponse> {
    return this.http.post(
      `${PLURAL_BASE}/switch`,
      input,
      householdMembershipMutationResponseSchema
    );
  }

  leave(householdId: string): Promise<HouseholdMembershipMutationResponse> {
    return this.http.post(
      `${PLURAL_BASE}/leave`,
      { householdId },
      householdMembershipMutationResponseSchema
    );
  }

  /**
   * Delete a household (manager only). Returns void — the legacy service
   * consumed the response envelope (`{ success, user?, sessionToken?,
   * loggedOut? }`) because the session can rotate when deleting the current
   * household. HttpClient.delete() discards the body today, so this method is
   * void.
   *
   * Tech debt: the T8 Settings screen that ports delete-household UX will
   * need the envelope. Options when that lands: (a) extend HttpClient with a
   * typed-DELETE helper that parses + returns a schema-validated body, or
   * (b) add a companion `removeAndBootstrap()` here that force-refetches
   * `fetchCurrent()` after delete (simpler, no HttpClient change). Deferred
   * out of T7 scope.
   */
  remove(
    householdId: string,
    options?: { deleteMembers?: boolean }
  ): Promise<void> {
    const query = options?.deleteMembers ? '?delete_members=true' : '';
    return this.http.delete(
      `${PLURAL_BASE}/${encodeURIComponent(householdId)}${query}`
    );
  }
}
